import { processPresetsPolyfill } from './utils'
import type { IPolyfillLogger, PresetPolyfillData } from './types'

/**
 * Window-resize observer for builder components polyfill.
 * Re-runs the presets polyfill on every resize so container-style-query
 * fallbacks pick up new computed values when the viewport changes.
 */
export class BuilderComponentResizeObserver {
	private windowResizeHandler: (() => void) | null = null
	private isListening = false

	constructor(
		private readonly getPresetsPolyfillData: () => Record<string, PresetPolyfillData>,
		private readonly logger?: IPolyfillLogger
	) {
		this.windowResizeHandler = () => {
			let interactionStarted = false
			let componentsCount = 0

			try {
				const processedPolyfillData = this.getPresetsPolyfillData()
				componentsCount = Object.keys(processedPolyfillData).length

				this.logger?.interactionStarted('builder-component-resize-polyfill', {
					customParams: { componentsCount },
				})
				interactionStarted = true

				processPresetsPolyfill(processedPolyfillData)
			} catch (error) {
				this.logger?.captureError(error as Error, {
					tags: { feature: 'builder-component', method: 'windowResizeHandler' },
				})
			} finally {
				if (interactionStarted) {
					this.logger?.interactionEnded('builder-component-resize-polyfill', {
						customParams: { componentsCount },
					})
				}
			}
		}
	}

	/** Start listening to window resize events. Idempotent. */
	startListening(): void {
		if (!this.isListening && this.windowResizeHandler) {
			window.addEventListener('resize', this.windowResizeHandler)
			this.isListening = true
		}
	}

	/** Stop listening and release all resources. Idempotent. */
	dispose(): void {
		if (this.isListening && this.windowResizeHandler) {
			window.removeEventListener('resize', this.windowResizeHandler)
			this.isListening = false
		}
		this.windowResizeHandler = null
	}
}

export const builderComponentPolyfillResizeObserver = (
	getPresetsPolyfillData: () => Record<string, PresetPolyfillData>,
	logger?: IPolyfillLogger
): BuilderComponentResizeObserver => {
	const resizeObserver = new BuilderComponentResizeObserver(getPresetsPolyfillData, logger)
	resizeObserver.startListening()
	return resizeObserver
}
